import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "@i18n";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import pageBreak from "../src/assets/icons/svg/pageBreak.svg";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type { ParamRule } from "./routeTypes";

/** uuid v4 as it appears in a URL. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a URL value satisfies the rule its route declared for it. */
function satisfies(value: string, rule: ParamRule): boolean {
  switch (rule) {
    case "objectId":
      return objectIdString.safeParse(value).success;
    case "uuid":
      return UUID_PATTERN.test(value);
    case "slug":
      // No format to check — a settings tab, a module name or a share token is opaque. Still
      // rejected when empty, which is the one malformed shape a slug can take in a URL.
      return value.length > 0;
  }
}

/**
 * Every param a route declares is checked against its own rule; `routeTypes.ts` makes that
 * declaration exhaustive, so no param reaches here unclassified.
 */
export function findMalformedParam(
  params: Readonly<Record<string, string | undefined>>,
  rules: Readonly<Record<string, ParamRule>>,
): string | null {
  for (const [name, rule] of Object.entries(rules)) {
    const value = params[name];
    // An absent optional param (`:settingType?`) is not malformed — it was simply not supplied.
    if (value === undefined) continue;
    if (!satisfies(value, rule)) return name;
  }
  return null;
}

/**
 * Same rules for query-string values, with one difference: a query param is optional by nature, so
 * ABSENT is always fine and only a present-but-malformed value blocks.
 */
export function findMalformedSearchParam(
  search: URLSearchParams,
  rules: Readonly<Record<string, ParamRule>>,
): string | null {
  for (const [name, rule] of Object.entries(rules)) {
    const value = search.get(name);
    if (value === null) continue;
    if (!satisfies(value, rule)) return name;
  }
  return null;
}

function RouteParamGuard({
  children,
  params: rules,
  searchParams: searchRules,
}: {
  children: React.ReactNode;
  params?: Readonly<Record<string, ParamRule>>;
  searchParams?: Readonly<Record<string, ParamRule>>;
}) {
  const params = useParams();
  const [search] = useSearchParams();
  const { t } = useTranslation();
  const malformed =
    (rules ? findMalformedParam(params, rules) : null) ??
    (searchRules ? findMalformedSearchParam(search, searchRules) : null);

  if (!malformed) return children;

  return (
    <GridPageLayout showHeader={false}>
      <div className="h-full justify-center bg-white items-center flex gap-5 flex-col w-full">
        <div className="flex flex-col gap-2 items-center justify-center">
          <img src={pageBreak} alt="pageBreak" />
          <span className="font-semibol text-2xl">{t("Invalid URL")}</span>
          <span>{t("This page link is malformed or broken")}</span>
          <span>{t("Please check the link and try again")}</span>
        </div>
      </div>
    </GridPageLayout>
  );
}

export default RouteParamGuard;
